import { generateContainer, generatePreview } from './providers.preview';
import {
  SupportedPlugins,
  TemplateOptions,
  ConfigOptions,
} from './providers.types';
import Instagram from './instagram';
import { loadingSvg } from '../icon';
import UserConsent from '../user-consents';
import Facebook from './facebook';
import Twitter from './twitter';
import Youtube from './youtube';
import Giphy from './giphy';
import Tagboard from './tagboard';

const providers = {
  instagram: Instagram(),
  facebook: Facebook(),
  twitter: Twitter(),
  youtube: Youtube(),
  giphy: Giphy(),
  tagboard: Tagboard(),
};

export default function Providers(consentHandler, querySelector?: String) {
  const previews = {};
  const rootEl = queryRootElement(querySelector);
  const consents = UserConsent(consentHandler);

  return Object.freeze({
    process,
    list: Object.keys(providers) as [SupportedPlugins],
    consents,
  });

  async function process(
    name: SupportedPlugins,
    { autoLoad, callToAction, privacyNotes }: ConfigOptions
  ) {
    try {
      const embeds = rootEl.querySelectorAll(`[data-social-${name}]`);

      embeds.forEach(async (element: Element) => {
        const url = element.getAttribute(`data-social-${name}`);

        if (autoLoad) {
          generateContainer(name, element);
          await mount(name, element, url);
        } else {
          await mountPreview(name, element, {
            url,
            callToAction,
            privacyNotes,
          });
        }
      });
    } catch (err) {
      console.log(err);
    }
  }

  async function mountPreview(
    name: SupportedPlugins,
    element,
    options: TemplateOptions
  ) {
    const { content, button, link } = generatePreview(name, element, {
      ...options,
      icon: name,
    });

    previews[name] = previews[name] || [];
    previews[name].push(async () => {
      try {
        content.innerHTML = loadingSvg;
        await mount(name, element, options.url);
      } catch {
        await mountPreview(name, element, options);
      }
    });

    // wait for clicks
    button.addEventListener('click', async () => {
      let names = [name];
      let grantConsents = [];

      if (name === 'tagboard') {
        grantConsents = [
          consents.grantConsent('facebook'),
          consents.grantConsent('twitter'),
          consents.grantConsent('instagram'),
        ];
      } else {
        grantConsents = [consents.grantConsent(name)];
      }

      await Promise.all(grantConsents); // grant consent from now on
      let loadingPosts = previews[name].map((load) => load()); // load all posts from the same consent

      if (name === 'tagboard') {
        names = names.concat(['facebook', 'instagram', 'twitter']);
        loadingPosts = loadingPosts.concat([
          previews['facebook'].map((load) => load()),
          previews['twitter'].map((load) => load()),
          previews['instagram'].map((load) => load()),
        ]);
      } else {
        const facebook = consents.hasConsent('facebook');
        const twitter = consents.hasConsent('twitter');
        const instagram = consents.hasConsent('instagram');

        if (facebook && twitter && instagram) {
          names.push('tagboard');

          loadingPosts = loadingPosts.concat(
            previews['tagboard'].map((load) => load())
          );
        }
      }

      await Promise.all(loadingPosts); // wait for all posts load
      names.forEach((_name) => (previews[_name] = []));
    });

    if (link) {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        consents.openConsents();
      });
    }
  }
}

async function mount(name: SupportedPlugins, element, url) {
  const provider = providers[name];
  if (provider) {
    await provider.mount(element, url);
  }
}

function queryRootElement(querySelector) {
  if (typeof querySelector === 'string') {
    const rootEl = document.querySelector(querySelector);
    if (rootEl) return rootEl;
  }

  return document;
}
